<template>
  <SuccessErrorModal
    :show-modal="showModal"
    :title="modalData.title"
    :description="modalData.description"
    :isError="modalData.isError"
    @close="showModal = false"
  />

  <Card class="rounded-2xl p-0 shadow-md">
    <CardHeader class="flex flex-row items-center justify-between p-6">
      <CardTitle class="text-[20px] font-semibold">Statement</CardTitle>
      <div class="flex gap-2">
        <Button
          variant="outline"
          size="icon"
          class="rounded-lg"
          @click="sendStatementEmail"
          :disabled="isSending || noPayments"
        >
          <Loader2 v-if="isSending" class="h-4 w-4 animate-spin" />
          <Mail v-else class="h-4 w-4" />
        </Button>
        <Button variant="outline" size="icon" class="rounded-lg" @click="exportPaymentsToCSV" :disabled="noPayments">
          <Upload class="h-4 w-4" />
        </Button>
      </div>
    </CardHeader>
    <CardContent class="p-0">
      <div v-if="noPayments" class="p-6">
        <p class="text-center text-sm text-grey-light-400">No payments to displayed for {{ order?.state }} order</p>
      </div>
      <Table v-else>
        <TableHeader>
          <TableRow class="bg-grey-light-50 border-t">
            <TableHead class="text-[14px] font-medium text-grey-light-700 pl-6">Date</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700 text-right">Amount</TableHead>
            <TableHead v-if="!isMobileOrIsTable" class="text-[14px] font-medium text-grey-light-700">Type</TableHead>
            <TableHead v-if="!isMobileOrIsTable" class="text-[14px] font-medium text-grey-light-700">Method</TableHead>
            <TableHead class="text-[14px] font-medium text-grey-light-700 pr-6">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="payment in payments" :key="payment.id" class="border-b border-grey-light-100">
            <TableCell class="text-[14px] text-grey-light-900 pl-6">{{ payment.date }}</TableCell>
            <TableCell class="text-[14px] text-grey-light-900 text-right">{{
              displayCurrency(payment.amount)
            }}</TableCell>
            <TableCell v-if="!isMobileOrIsTable" class="text-[14px] text-grey-light-900">{{ payment.type }}</TableCell>
            <TableCell v-if="!isMobileOrIsTable" class="text-[14px] text-grey-light-900">{{
              payment.method
            }}</TableCell>
            <TableCell class="pr-6">
              <Badge
                :variant="getPaymentStatusVariant(payment.status)"
                class="rounded-full"
                :class="getPaymentStatusClasses(payment.status)"
              >
                {{ payment.status }}
              </Badge>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import SuccessErrorModal from '@/components/common/SuccessErrorModal.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { IPaymentReport } from '@/models/payments';
import { displayCurrency } from '@/utils/format.utils';
import { getPaymentStatusClasses, getPaymentStatusVariant } from '@/utils/payment.utils';
import { useBreakpoint } from '@/utils/useBreakPoint';
import { Loader2, Mail, Upload } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import type { ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  payments: IPaymentReport[];
  order?: ModelOrderResponse;
}>();

const { isMobile, isTablet } = useBreakpoint();
const isMobileOrIsTable = computed(() => isMobile.value || isTablet.value);

const showModal = ref(false);
const isSending = ref(false);
const modalData = ref({
  title: '',
  description: '',
  isError: false,
});

const noPayments = computed(() => !props.payments || props.payments.length === 0);

const sendStatementEmail = async () => {};

const exportPaymentsToCSV = () => {};
</script>
